import { Fragment, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, CheckCircle, Smartphone, Camera, Trash2, Upload, AlertCircle } from "lucide-react";
import { api, apiError } from "@/api";

const IPHONE_MODELS = [
  "iPhone 16 Pro Max","iPhone 16 Pro","iPhone 16 Plus","iPhone 16",
  "iPhone 15 Pro Max","iPhone 15 Pro","iPhone 15 Plus","iPhone 15",
  "iPhone 14 Pro Max","iPhone 14 Pro","iPhone 14 Plus","iPhone 14",
  "iPhone 13 Pro Max","iPhone 13 Pro","iPhone 13 Mini","iPhone 13",
  "iPhone 12 Pro Max","iPhone 12 Pro","iPhone 12 Mini","iPhone 12",
  "iPhone 11 Pro Max","iPhone 11 Pro","iPhone 11",
  "iPhone SE (3rd Gen)","iPhone SE (2nd Gen)",
  "iPhone XS Max","iPhone XS","iPhone XR","iPhone X",
  "Other iPhone",
];
const STORAGES = ["32GB","64GB","128GB","256GB","512GB","1TB"];
const COLORS = ["Space Black","Silver","Gold","Deep Purple","Blue","Green","Pink","Midnight","Starlight","Red"];
const CONDITIONS = [
  { value:"excellent", icon:"✨", label:"Excellent", desc:"Like new, no visible scratches" },
  { value:"good",      icon:"👍", label:"Good",      desc:"Minor scratches, fully functional" },
  { value:"fair",      icon:"🔧", label:"Fair",      desc:"Visible wear, works fine" },
  { value:"poor",      icon:"⚠️", label:"Poor",      desc:"Heavy damage or issues" },
];
const ISSUES = [
  "Screen damage","Battery issues","Camera issues","Charging port issues",
  "Face ID issues","Touch ID issues","Speaker issues","Microphone issues",
  "Water damage","Back glass crack","Frame damage","Software issues",
];

const STEPS = ["Device","Condition","Photos","Contact"];

/* ── Steps Indicator ── */
function StepsBar({ step }) {
  return (
    <div className="phi-sell-steps-indicator">
      {STEPS.map((label, i) => (
        <Fragment key={label}>
          <div className={`phi-step-dot${step > i ? " done" : step === i ? " active" : ""}`} title={label}>
            {step > i ? <CheckCircle size={14}/> : i+1}
          </div>
          {i < STEPS.length - 1 && <div className={`phi-step-line${step > i ? " done" : ""}`}/>}
        </Fragment>
      ))}
    </div>
  );
}

/* ── Required badge ── */
const Req = () => <span style={{ color:"#ef4444", fontSize:10, marginLeft:4, fontWeight:700 }}>*Required</span>;

/* ══ Step 1: Device ══════════════════════════════ */
function StepDevice({ form, setForm, onNext }) {
  const errors = {
    model: !form.model,
    storage: !form.storage,
    color: !form.color,
  };
  const valid = !errors.model && !errors.storage && !errors.color;
  return (
    <div className="phi-sell-form-card">
      <h3 className="phi-form-title">Select Your iPhone</h3>
      <p className="phi-form-sub">Sahi model, storage aur color choose karo accurate quote ke liye</p>

      <div className="phi-form-group">
        <label>iPhone Model <Req/></label>
        <select
          className={`phi-form-input phi-form-select${errors.model && form._touched ? " phi-input-error" : ""}`}
          value={form.model}
          onChange={(e) => setForm({ ...form, model: e.target.value })}
          data-testid="sell-model-select"
        >
          <option value="">-- iPhone Model Select Karo --</option>
          {IPHONE_MODELS.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        {errors.model && form._touched && <p className="phi-field-error"><AlertCircle size={12}/> Model select karna zaroori hai</p>}
      </div>

      <div className="phi-form-group">
        <label>Storage <Req/></label>
        <div className="phi-pills-row">
          {STORAGES.map((s) => (
            <button key={s} type="button"
              className={`phi-pill${form.storage === s ? " on" : ""}${errors.storage && form._touched ? " phi-pill-error" : ""}`}
              onClick={() => setForm({ ...form, storage: s })}
              data-testid={`sell-storage-${s}`}
            >{s}</button>
          ))}
        </div>
        {errors.storage && form._touched && <p className="phi-field-error"><AlertCircle size={12}/> Storage select karna zaroori hai</p>}
      </div>

      <div className="phi-form-group">
        <label>Color <Req/></label>
        <div className="phi-pills-row">
          {COLORS.map((c) => (
            <button key={c} type="button"
              className={`phi-pill${form.color === c ? " on" : ""}${errors.color && form._touched ? " phi-pill-error" : ""}`}
              onClick={() => setForm({ ...form, color: c })}
              data-testid={`sell-color-${c.toLowerCase().replace(/\s/g,"-")}`}
            >{c}</button>
          ))}
        </div>
        {errors.color && form._touched && <p className="phi-field-error"><AlertCircle size={12}/> Color select karna zaroori hai</p>}
      </div>

      <div className="phi-sell-nav">
        <button className="phi-sell-next-btn" onClick={() => {
          if (!valid) { setForm({ ...form, _touched: true }); toast.error("Sab required fields fill karo"); return; }
          onNext();
        }} data-testid="sell-step1-next">
          Aage Jao: Condition →
        </button>
      </div>
    </div>
  );
}

/* ══ Step 2: Condition ═══════════════════════════ */
function StepCondition({ form, setForm, onNext, onBack }) {
  const toggleIssue = (issue) =>
    setForm((f) => ({ ...f, issues: f.issues.includes(issue) ? f.issues.filter((i) => i !== issue) : [...f.issues, issue] }));
  const valid = !!form.condition;
  return (
    <div className="phi-sell-form-card">
      <h3 className="phi-form-title">Device ki Condition Batao</h3>
      <p className="phi-form-sub">Honest condition batane se best quote milta hai</p>

      <div className="phi-form-group">
        <label>Overall Condition <Req/></label>
        {!form.condition && form._touched2 && <p className="phi-field-error"><AlertCircle size={12}/> Condition select karna zaroori hai</p>}
        <div className="phi-condition-cards">
          {CONDITIONS.map((c) => (
            <div key={c.value}
              className={`phi-condition-card${form.condition === c.value ? " on" : ""}`}
              onClick={() => setForm({ ...form, condition: c.value })}
              data-testid={`sell-condition-${c.value}`}
            >
              <span className="cc-icon">{c.icon}</span>
              <b>{c.label}</b>
              <small>{c.desc}</small>
            </div>
          ))}
        </div>
      </div>

      <div className="phi-form-group">
        <label>Known Issues <span style={{ color:"#6b7f9e", fontSize:10, fontWeight:500 }}>(Optional — sab select karo jo applicable ho)</span></label>
        <div className="phi-issues-grid">
          {ISSUES.map((issue) => (
            <div key={issue}
              className={`phi-issue-check${form.issues.includes(issue) ? " on" : ""}`}
              onClick={() => toggleIssue(issue)}
              data-testid={`sell-issue-${issue.toLowerCase().replace(/\s/g,"-")}`}
            >
              <div className="phi-issue-check-box">{form.issues.includes(issue) && <CheckCircle size={10}/>}</div>
              {issue}
            </div>
          ))}
        </div>
      </div>

      <div className="phi-form-group">
        <label>Additional Details <span style={{ color:"#6b7f9e", fontSize:10, fontWeight:500 }}>(Optional)</span></label>
        <textarea className="phi-form-input" rows={3}
          placeholder="Koi aur detail jo batana chahte ho device ke baare mein..."
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          data-testid="sell-description" style={{ resize:"vertical" }}
        />
      </div>

      <div className="phi-sell-nav">
        <button className="phi-sell-back-btn" onClick={onBack} data-testid="sell-step2-back">← Wapas</button>
        <button className="phi-sell-next-btn" onClick={() => {
          if (!valid) { setForm({ ...form, _touched2: true }); toast.error("Condition select karna zaroori hai"); return; }
          onNext();
        }} data-testid="sell-step2-next">
          Aage Jao: Photos →
        </button>
      </div>
    </div>
  );
}

/* ══ Step 3: Photo Upload ════════════════════════ */
const PHOTO_SLOTS = [
  { key:"front", label:"Front Screen",    hint:"Screen clearly dikhe", required:true },
  { key:"back",  label:"Back Panel",      hint:"Back cover clearly dikhe", required:true },
  { key:"extra1",label:"Side / Frame",    hint:"Frame / sides optional", required:false },
  { key:"extra2",label:"Damage (if any)", hint:"Any damage close-up", required:false },
];

function PhotoSlot({ slot, file, onAdd, onRemove }) {
  const inputRef = useRef(null);
  const preview = file ? URL.createObjectURL(file) : null;
  return (
    <div className={`phi-photo-slot${file ? " has-photo" : ""}${slot.required ? " required" : ""}`}
      onClick={() => !file && inputRef.current?.click()}
      data-testid={`sell-photo-slot-${slot.key}`}
    >
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp"
        style={{ display:"none" }}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onAdd(f); e.target.value=""; }}
        data-testid={`sell-photo-input-${slot.key}`}
      />
      {file ? (
        <>
          <img src={preview} alt={slot.label} className="phi-photo-preview"/>
          <button className="phi-photo-remove" onClick={(e) => { e.stopPropagation(); onRemove(); }}
            data-testid={`sell-photo-remove-${slot.key}`}
          ><Trash2 size={14}/></button>
          <span className="phi-photo-slot-label added">{slot.label} ✓</span>
        </>
      ) : (
        <>
          <div className="phi-photo-slot-icon">
            <Camera size={22}/>
          </div>
          <span className="phi-photo-slot-label">{slot.label}</span>
          <span className="phi-photo-slot-hint">{slot.hint}</span>
          {slot.required && <span className="phi-photo-required-badge">Required</span>}
        </>
      )}
    </div>
  );
}

function StepPhotos({ form, setForm, onNext, onBack }) {
  const photos = form.photos || {};
  const setPhoto = (key, file) => setForm((f) => ({ ...f, photos: { ...f.photos, [key]: file } }));
  const removePhoto = (key) => setForm((f) => { const p = { ...f.photos }; delete p[key]; return { ...f, photos: p }; });

  const frontOk = !!photos.front;
  const backOk  = !!photos.back;
  const valid   = frontOk && backOk;

  return (
    <div className="phi-sell-form-card">
      <h3 className="phi-form-title">Device ki Photos Upload Karo</h3>
      <p className="phi-form-sub">
        Front aur back ki photo <b style={{ color:"#00d4ff" }}>mandatory hai</b>. Clear photos se fast quote milta hai.
      </p>

      <div className="phi-photo-grid">
        {PHOTO_SLOTS.map((slot) => (
          <PhotoSlot
            key={slot.key}
            slot={slot}
            file={photos[slot.key] || null}
            onAdd={(file) => setPhoto(slot.key, file)}
            onRemove={() => removePhoto(slot.key)}
          />
        ))}
      </div>

      {/* Tip */}
      <div className="phi-photo-tip" data-testid="sell-photo-tip">
        <Camera size={14} style={{ color:"#00d4ff", flexShrink:0 }}/>
        <span>Achhi lighting mein photos lo — blurry photos se offer kam ho sakta hai</span>
      </div>

      {/* Validation hint */}
      {(!frontOk || !backOk) && (
        <div className="phi-photo-validation" data-testid="sell-photo-validation">
          <AlertCircle size={14}/>
          <span>
            {!frontOk && !backOk ? "Front screen aur back panel ki photo required hai" :
             !frontOk ? "Front screen ki photo required hai" :
             "Back panel ki photo required hai"}
          </span>
        </div>
      )}

      <div className="phi-sell-nav">
        <button className="phi-sell-back-btn" onClick={onBack} data-testid="sell-step3-back">← Wapas</button>
        <button className="phi-sell-next-btn" onClick={() => {
          if (!valid) { toast.error("Front aur back ki photo upload karna zaroori hai!"); return; }
          onNext();
        }} disabled={!valid} data-testid="sell-step3-next">
          Aage Jao: Contact →
        </button>
      </div>
    </div>
  );
}

/* ══ Step 4: Contact & Submit ════════════════════ */
function StepContact({ form, setForm, onBack, onSubmit, busy }) {
  const validPhone = /^[6-9][0-9]{9}$/.test(form.contact_phone);
  const photos = form.photos || {};
  const photoCount = Object.keys(photos).length;

  return (
    <div className="phi-sell-form-card">
      <h3 className="phi-form-title">Contact Details</h3>
      <p className="phi-form-sub">24 ghante mein best offer aapke phone par aayega</p>

      <div className="phi-form-group">
        <label>Mobile Number <Req/></label>
        <input type="tel" className="phi-form-input" placeholder="10-digit mobile number"
          maxLength={10} value={form.contact_phone}
          onChange={(e) => setForm({ ...form, contact_phone: e.target.value.replace(/\D/g,"") })}
          data-testid="sell-contact-phone"
        />
        {form.contact_phone && !validPhone && (
          <p className="phi-field-error"><AlertCircle size={12}/> Valid 10-digit mobile number daalo</p>
        )}
      </div>

      {/* Full Summary */}
      <div className="phi-summary-box" data-testid="sell-summary-box">
        <p className="phi-summary-title">Request Summary</p>
        <div className="phi-summary-row"><span>iPhone</span><b>{form.model} {form.storage} {form.color}</b></div>
        <div className="phi-summary-row"><span>Condition</span><b>{CONDITIONS.find((c) => c.value === form.condition)?.icon} {CONDITIONS.find((c) => c.value === form.condition)?.label}</b></div>
        <div className="phi-summary-row"><span>Issues</span><b>{form.issues?.length ? form.issues.slice(0,3).join(", ") + (form.issues.length > 3 ? ` +${form.issues.length-3}` : "") : "None"}</b></div>
        <div className="phi-summary-row"><span>Photos</span>
          <b style={{ color: photoCount >= 2 ? "#22c55e" : "#f59e0b" }}>
            {photoCount} uploaded {photoCount >= 2 ? "✓" : "(min 2 required)"}
          </b>
        </div>
      </div>

      <div className="phi-sell-nav">
        <button className="phi-sell-back-btn" onClick={onBack} data-testid="sell-step4-back">← Wapas</button>
        <button className="phi-sell-next-btn" onClick={onSubmit}
          disabled={!validPhone || busy} data-testid="sell-submit-btn"
        >
          {busy ? (
            <span style={{ display:"flex", alignItems:"center", gap:8 }}>
              <span className="phi-spin"/> Submitting…
            </span>
          ) : "Quote Maango →"}
        </button>
      </div>
    </div>
  );
}

/* ══ Success Screen ══════════════════════════════ */
const SELL_TIMELINE = [
  { icon: "✓",  label: "Request Submit",      sub: "Abhi ho gaya",            done: true  },
  { icon: "🔍", label: "Team Review Karegi",  sub: "2–6 ghante mein",         active: true },
  { icon: "💬", label: "Offer Bhejenge",       sub: "24 ghante ke andar",      done: false  },
  { icon: "✅", label: "Aap Accept/Reject Karo", sub: "Account → Sell Requests", done: false },
  { icon: "💰", label: "Payment Receive Karo", sub: "Accept ke 1-2 din baad",  done: false  },
];

function SuccessScreen({ requestNumber }) {
  return (
    <div className="phi-sell-form-card phi-success-card" data-testid="sell-success-screen">
      {/* Animated checkmark */}
      <div className="phi-success-wrap">
        <div className="phi-success-icon">✓</div>
        <div className="phi-success-ring phi-ring-a" />
        <div className="phi-success-ring phi-ring-b" />
      </div>

      <h2 style={{ fontSize: 22, fontWeight: 800, color: "#e8f4fd", margin: "0 0 6px" }}>
        Request Submit Ho Gayi!
      </h2>
      <p style={{ color: "#6b7f9e", fontSize: 13, margin: "0 0 14px", lineHeight: 1.5 }}>
        Humari team aapke iPhone ki details review karegi aur 24 ghante mein offer bhejegi.
      </p>

      <div className="phi-request-number" data-testid="sell-request-number">
        Request # <strong style={{ color: "#00d4ff" }}>{requestNumber}</strong>
      </div>

      {/* Tracking Timeline */}
      <div className="phi-sell-timeline">
        {SELL_TIMELINE.map((step, i) => (
          <div key={i} className={`phi-tl-step${step.done ? " phi-tl-done" : step.active ? " phi-tl-active" : ""}`}>
            <div className="phi-tl-left">
              <div className="phi-tl-dot">{step.done ? "✓" : ""}</div>
              {i < SELL_TIMELINE.length - 1 && <div className="phi-tl-connector" />}
            </div>
            <div className="phi-tl-body">
              <b>{step.label}</b>
              <span>{step.sub}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Tip */}
      <div className="phi-success-tip">
        <span className="phi-tip-icon">💡</span>
        <p>Offer mile? <b>Account → Sell Requests</b> mein jaao aur wahan accept ya reject karo.</p>
      </div>

      <div style={{ display:"flex", gap:12, justifyContent:"center", flexWrap:"wrap", marginTop:20 }}>
        <Link to="/" className="phi-btn-primary" data-testid="sell-success-home">Shop Par Jao</Link>
        <Link to="/account" className="phi-btn-outline" data-testid="sell-success-account">Track Karo</Link>
      </div>
    </div>
  );
}

/* ══ Main SellPage ═══════════════════════════════ */
export default function SellPage({ common }) {
  const navigate = useNavigate();
  const user = common?.user;
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [requestNumber, setRequestNumber] = useState(null);
  const [form, setForm] = useState({
    model:"", storage:"", color:"",
    condition:"", issues:[], description:"",
    contact_phone:"",
    photos: {},          // { front: File, back: File, extra1?: File, extra2?: File }
    _touched: false,
    _touched2: false,
  });

  const handleSubmit = async () => {
    if (!user) { toast.error("Please sign in karo pehle"); navigate("/login?next=/sell"); return; }

    setBusy(true);
    try {
      const payload = {
        brand:"Apple", model:form.model, storage:form.storage,
        color:form.color, condition:form.condition,
        issues:form.issues, description:form.description,
        contact_phone:form.contact_phone,
      };

      const fd = new FormData();
      fd.append("input", JSON.stringify(payload));

      // Attach photos — front + back mandatory
      const photoKeys = ["front","back","extra1","extra2"];
      for (const key of photoKeys) {
        if (form.photos[key]) fd.append("photos", form.photos[key], `${key}.jpg`);
      }

      const { data } = await api.post("/api/sell-requests", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setRequestNumber(data.request_number || data.id?.slice(-8).toUpperCase());
      setStep(4);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const Topbar = common?.Topbar;
  const FooterBar = common?.FooterBar;
  const BottomNav = common?.BottomNav;

  return (
    <>
      {Topbar && <Topbar {...(common || {})}/>}
      <div className="phi-sell-page">
        {/* Hero */}
        <div className="phi-sell-page-hero">
          {step < 4 && (
            <Link to="/" style={{ display:"inline-flex", alignItems:"center", gap:6, color:"#6b7f9e", fontSize:12, textDecoration:"none", marginBottom:16 }} data-testid="sell-page-back">
              <ChevronLeft size={14}/> Shop Par Wapas
            </Link>
          )}
          <Smartphone size={32} style={{ color:"var(--neon-blue)", display:"block", margin:"0 auto 10px" }}/>
          <h1>{step === 4 ? "Done!" : "Apna iPhone Becho"}</h1>
          <p>{step === 4 ? "Request receive ho gayi hai" : "Best price, fast process. 4 aasaan steps."}</p>
          {step < 4 && <StepsBar step={step}/>}
        </div>

        {/* Form */}
        <div className="phi-sell-form-wrap">
          {step === 0 && <StepDevice form={form} setForm={setForm} onNext={() => setStep(1)}/>}
          {step === 1 && <StepCondition form={form} setForm={setForm} onNext={() => setStep(2)} onBack={() => setStep(0)}/>}
          {step === 2 && <StepPhotos form={form} setForm={setForm} onNext={() => setStep(3)} onBack={() => setStep(1)}/>}
          {step === 3 && <StepContact form={form} setForm={setForm} onBack={() => setStep(2)} onSubmit={handleSubmit} busy={busy}/>}
          {step === 4 && <SuccessScreen requestNumber={requestNumber}/>}
        </div>
      </div>
      {FooterBar && <FooterBar/>}
      {BottomNav && <BottomNav active=""/>}
    </>
  );
}
